import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  ClockCounterClockwise,
  ShieldCheck,
  FileText,
  Star,
  ArrowClockwise,
} from "@phosphor-icons/react";

const ONBOARDING_LABELS = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
  blocked: "Blocked",
};

const CATEGORY_STYLES = {
  onboarding: { iconBg: "bg-blue-50", iconText: "text-blue-600" },
  compliance: { iconBg: "bg-emerald-50", iconText: "text-emerald-600" },
  rating: { iconBg: "bg-amber-50", iconText: "text-amber-600" },
  score: { iconBg: "bg-slate-100", iconText: "text-slate-600" },
  default: { iconBg: "bg-slate-100", iconText: "text-slate-500" },
};

function describeAction(action) {
  if (action.startsWith("onboarding.")) {
    const target = action.split(".")[1];
    return {
      text: `Onboarding set to ${ONBOARDING_LABELS[target] || target}`,
      Icon: ShieldCheck,
      ...CATEGORY_STYLES.onboarding,
    };
  }
  if (action.startsWith("compliance.")) {
    const [, doc, verb] = action.split(".");
    const label = (doc || "").toUpperCase();
    const verbText =
      verb === "upload" ? "document uploaded" : verb === "delete" ? "document removed" : "details updated";
    return { text: `${label} ${verbText}`, Icon: FileText, ...CATEGORY_STYLES.compliance };
  }
  if (action === "rating.manual") {
    return { text: "Manual rating updated", Icon: Star, ...CATEGORY_STYLES.rating };
  }
  if (action === "score.refresh") {
    return { text: "Auto-score refreshed", Icon: ArrowClockwise, ...CATEGORY_STYLES.score };
  }
  return { text: action, Icon: ClockCounterClockwise, ...CATEGORY_STYLES.default };
}

export default function VendorAuditHistory({ vendorId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vendors/${vendorId}/audit-history`);
      setHistory(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) load();
  }, [vendorId, load]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <ClockCounterClockwise size={16} weight="duotone" className="text-slate-500" />
        <span className="text-sm font-semibold text-slate-900">Activity</span>
        <span className="text-[11px] text-slate-500 ml-auto">Last 100 events</span>
      </div>
      <div className="max-h-[32rem] overflow-y-auto divide-y divide-slate-100" data-testid="audit-history-list">
        {loading && <div className="p-5 text-sm text-slate-400">Loading…</div>}
        {!loading && history.length === 0 && (
          <div className="p-5 text-sm text-slate-400">No activity yet.</div>
        )}
        {history
          .slice()
          .sort((a, b) => new Date(b.at) - new Date(a.at))
          .map((h, i) => {
            const { text, Icon, iconBg, iconText } = describeAction(h.action || "");
            return (
              <div key={i} className="px-5 py-3 flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full ${iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <Icon size={13} weight="bold" className={iconText} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 font-medium">{text}</div>
                  {h.notes && <div className="text-xs text-slate-500 truncate">{h.notes}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {h.by} · {new Date(h.at).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
